
import React, { useState } from 'react';
import { ApprovalFlow, ApprovalStage } from '../../types/approval';
import { useAuth } from '../../context/AuthContext';
import { isLegal, isLisa, isIrene, getDeptCodeByEmail, canViewAllData } from '../../config/roles';
import { CO_REVIEW_DEPT_OPTIONS } from '../../constants/coReviewMap';
import { getDeptNameByCode } from '../../constants/departments';
import { FileUploadKit } from '../common/FileUploadKit';
import { uploadApprovalFile, updateApprovalRequest } from '../../services/approvalRequestsService';
import type { AttachmentInfo } from '../../types/legalOps';
import { useToast } from '../Toast';
// Fix import: Go up 3 levels from ui/components/approval/ to root
import { db, serverTimestamp, doc, setDoc, getDoc, updateDoc, collection } from '../../../firebaseClient';

interface Props {
  flow: ApprovalFlow | null;
  onDecision?: (stageKey: string, decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | 'DONE', comment: string, deptCode?: string) => void;
  requestId?: string;
  reviewAttachments?: AttachmentInfo[];
  kcAttachments?: AttachmentInfo[];
  onKcAttachmentsChange?: (files: AttachmentInfo[]) => void;
  onReviewAttachmentsChange?: (files: AttachmentInfo[]) => void;
}

const ALLOWED_EDITORS = ['kc@lg.app', 'lisa@lg.app', 'irene@lg.app'];

export const ApprovalFlowPanel: React.FC<Props> = ({ 
    flow, 
    onDecision, 
    requestId, 
    reviewAttachments = [], 
    kcAttachments = [], 
    onKcAttachmentsChange 
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [commentMap, setCommentMap] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [editingStageKey, setEditingStageKey] = useState<string | null>(null);

  if (!flow) {
    return (
      <div style={{padding: '24px', border: '1px dashed #444', borderRadius: '8px', color: '#888', textAlign: 'center', fontSize: '13px', background: 'rgba(255,255,255,0.02)'}}>
        <div style={{marginBottom: 8, fontSize: 16}}>📋 簽核流程</div>
        <div>尚未啟用簽核流程。</div>
        <div style={{fontSize: 12, marginTop: 4, opacity: 0.7}}>（可於編輯或送出時啟用）</div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
      switch(status) {
          case 'APPROVED': case 'COMPLETED': case 'DONE': return 'var(--ok)';
          case 'REJECTED': case 'RETURNED': return 'var(--danger)';
          case 'PENDING': return '#fbbf24'; // Yellow
          case 'NOT_STARTED': return 'var(--muted)';
          default: return 'var(--muted)';
      }
  };

  const getStatusLabel = (status: string) => {
      switch(status) {
          case 'APPROVED': return '已核准';
          case 'COMPLETED': return '流程完成';
          case 'REJECTED': return '已駁回';
          case 'RETURNED': return '退回';
          case 'PENDING': return '待審核';
          case 'DONE': return '已會辦';
          case 'NOT_STARTED': return '未開始';
          default: return status;
      }
  };

  // Helper: Get label for comment section based on role/stage
  const getCommentLabel = (stageKey: string, role: string) => {
      if (stageKey === 'LEGAL_REVIEW' || role === 'legal') return "法務意見";
      if (role === 'multi-dept') return "會辦意見";
      return "核決意見";
  };

  // Check if user is the current pending actor
  const canActOn = (stage: ApprovalStage) => {
      if (flow.currentStageKey !== stage.key) return false;
      if (stage.status !== 'PENDING') return false;

      const email = user?.email;
      if (stage.key === 'CO_REVIEW') return true; // Detailed check inside render
      if (stage.approverRole === 'legal') return isLegal(email);
      if (stage.approverRole === 'lisa') return isLisa(email);
      if (stage.approverRole === 'irene') return isIrene(email);
      return false;
  };

  // Check if user can EDIT a past decision
  const canEditDecision = (stage: ApprovalStage) => {
      if (!user?.email) return false;
      if (stage.status === 'PENDING' || stage.status === 'NOT_STARTED') return false; // Active/Future stages use standard input
      
      const email = user.email.toLowerCase();
      
      // Must be in whitelist
      if (!ALLOWED_EDITORS.includes(email)) return false;

      // Must be the one who acted on it (or authorized role)
      if (stage.approverRole === 'legal' && isLegal(email)) return true;
      if (stage.approverRole === 'lisa' && isLisa(email)) return true;
      if (stage.approverRole === 'irene' && isIrene(email)) return true;
      
      return false;
  };

  // --- Determine Visibility ---
  const isRequester = user?.id === flow.createdBy;
  const isLegalUser = isLegal(user?.email);
  const isApprover = isLisa(user?.email) || isIrene(user?.email);
  const isSuper = canViewAllData(user?.email);
  
  // Check Department / Co-Reviewer
  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameByCode(userDeptCode);
  
  const isSameDept = flow.createdDept === userDeptName;
  const isCoReviewer = flow.stages.some(s => s.coReviewDepts?.includes(userDeptCode.toLowerCase()));

  // Expanded visibility: Requester, Dept Members, Co-Reviewers, Legal, Approvers, Super
  const canViewAttachments = isRequester || isSameDept || isCoReviewer || isLegalUser || isApprover || isSuper;

  const handleKcFileUpload = async (files: File[]) => {
      if (!requestId || !user?.email) return;
      setUploading(true);
      try {
          const newAtts: AttachmentInfo[] = [];
          for (const f of files) {
              const meta = await uploadApprovalFile(requestId, 'review', f, user.email);
              newAtts.push(meta);
          }
          const updated = [...(kcAttachments || []), ...newAtts];
          await updateApprovalRequest(requestId, { kcAttachments: updated });
          if (onKcAttachmentsChange) onKcAttachmentsChange(updated);
          showToast("法務審查附件已上傳");
      } catch (e) {
          console.error(e);
          showToast("上傳失敗", "error");
      } finally {
          setUploading(false);
      }
  };

  const handleRemoveKcFile = async (idx: number) => {
      if (!requestId) return;
      if (!confirm("確定刪除此附件？")) return;
      const updated = [...(kcAttachments || [])];
      updated.splice(idx, 1);
      try {
          await updateApprovalRequest(requestId, { kcAttachments: updated });
          if (onKcAttachmentsChange) onKcAttachmentsChange(updated);
          showToast("附件已刪除");
      } catch(e) {
          showToast("刪除失敗", "error");
      }
  };

  const startEditing = (stage: ApprovalStage) => {
      setEditingStageKey(stage.key);
      setCommentMap({ ...commentMap, [stage.key]: stage.comment || '' });
  };
  
  const cancelEditing = () => {
      setEditingStageKey(null);
  };

  return (
    <div className="section" style={{background: '#1a1d24', padding: '16px', border: '1px solid var(--border)', borderRadius: 12}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #333'}}>
        <div style={{fontWeight: 'bold', color: '#fff', fontSize: 15}}>
            簽核流程 
            <span style={{fontSize:12, fontWeight:'normal', color:'#aaa', marginLeft:8}}>
                ({flow.createdDept || '部門未知'} - {flow.assigneeName || '承辦人未知'})
            </span>
        </div>
        <div className="badge" style={{borderColor: getStatusColor(flow.status), color: getStatusColor(flow.status)}}>
          {getStatusLabel(flow.status)}
        </div>
      </div>

      <div style={{display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative'}}>
        <div style={{position: 'absolute', left: '20px', top: '20px', bottom: '20px', width: '2px', background: '#333', zIndex: 0}}></div>

        {flow.stages.map((stage, idx) => {
          const isCurrent = flow.currentStageKey === stage.key;
          const statusColor = getStatusColor(stage.status);
          const isDone = stage.status === 'APPROVED' || stage.status === 'REJECTED' || stage.status === 'RETURNED';
          
          const isLegalReview = stage.key === 'LEGAL_REVIEW';
          const commentLabel = getCommentLabel(stage.key, stage.approverRole);
          const placeholder = isLegalReview 
            ? "請輸入本次合約審查重點、風險提醒或審核說明" 
            : `請輸入${commentLabel}...`;

          const userCanEdit = canEditDecision(stage);
          const isEditingThisStage = editingStageKey === stage.key;
          const userCanAct = canActOn(stage);

          // --- Special Rendering for CO_REVIEW ---
          if (stage.key === 'CO_REVIEW') {
              const currentUserDeptCodeLower = userDeptCode.toLowerCase();

              return (
                <div key={stage.key} style={{display: 'flex', gap: '12px', zIndex: 1, opacity: (!isCurrent && stage.status === 'PENDING') ? 0.5 : 1}}>
                    <div style={{
                        width: '42px', height: '42px', borderRadius: '50%', 
                        background: isCurrent ? '#2563eb' : (isDone ? '#10b981' : '#2a2a2a'),
                        border: `2px solid ${isCurrent ? '#60a5fa' : (isDone ? '#10b981' : '#444')}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', fontSize: '14px', color: '#fff', flexShrink: 0
                    }}>
                        {idx + 1}
                    </div>
                    <div style={{flex: 1, borderRadius: '8px', border: `1px solid ${isCurrent ? '#60a5fa' : '#333'}`, background: isCurrent ? 'rgba(37, 99, 235, 0.05)' : '#222', padding: '12px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                            <div style={{fontWeight: 500, fontSize: '14px', color: isCurrent ? '#60a5fa' : '#e5e7eb'}}>
                                {stage.label}
                            </div>
                            <div style={{fontSize: '12px', color: statusColor, fontWeight: 'bold'}}>
                                {getStatusLabel(stage.status)}
                            </div>
                        </div>
                        
                        {/* List of Depts Blocks */}
                        <div style={{display:'flex', flexDirection:'column', gap:12}}>
                            {stage.coReviewDepts?.map(deptId => {
                                const deptInfo = CO_REVIEW_DEPT_OPTIONS.find(d => d.code === deptId);
                                const deptName = deptInfo ? deptInfo.label : deptId.toUpperCase();
                                
                                const decision = stage.deptDecisions?.[deptId];
                                const isDeptDone = decision?.status === 'DONE';
                                const isMyDept = deptId.toLowerCase() === currentUserDeptCodeLower;
                                
                                // Determine if this specific block is editable by the current user
                                const canEditBlock = isCurrent && isMyDept && !isDeptDone && onDecision;
                                const commentKey = `${stage.key}_${deptId}`;

                                const blockStatusColor = isDeptDone ? 'var(--ok)' : (isCurrent ? '#fbbf24' : '#666');

                                return (
                                    <div key={deptId} style={{
                                        background: 'rgba(255,255,255,0.03)', 
                                        borderRadius: 6, 
                                        border: `1px solid ${canEditBlock ? '#60a5fa' : '#333'}`,
                                        overflow: 'hidden'
                                    }}>
                                        {/* Block Header */}
                                        <div style={{
                                            padding: '8px 12px', 
                                            background: 'rgba(0,0,0,0.2)', 
                                            borderBottom: '1px solid #333',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                        }}>
                                            <div style={{fontWeight: 'bold', fontSize: '13px', color: '#ddd'}}>
                                                {deptName}
                                            </div>
                                            <div style={{fontSize: '11px', color: blockStatusColor, fontWeight: 500}}>
                                                {isDeptDone ? '已會辦' : '待會辦'}
                                            </div>
                                        </div>

                                        {/* Block Content (Comment Input or Display) */}
                                        <div style={{padding: '12px'}}>
                                            <textarea
                                                className="input"
                                                placeholder={canEditBlock ? "請填寫部門會辦意見與建議 (必填)..." : "(尚未填寫意見)"}
                                                rows={canEditBlock ? 3 : 2}
                                                value={canEditBlock ? (commentMap[commentKey] || '') : (decision?.comment || '')}
                                                onChange={(e) => setCommentMap({...commentMap, [commentKey]: e.target.value})}
                                                disabled={!canEditBlock}
                                                style={{
                                                    width: '100%', 
                                                    marginBottom: canEditBlock ? '8px' : '0', 
                                                    fontSize: '13px', 
                                                    background: !canEditBlock ? 'transparent' : '#0b0b0c',
                                                    border: !canEditBlock ? 'none' : '1px solid var(--border)',
                                                    padding: !canEditBlock ? '0' : '8px',
                                                    color: !canEditBlock ? '#aaa' : '#fff',
                                                    resize: canEditBlock ? 'vertical' : 'none',
                                                    minHeight: 'auto'
                                                }}
                                            />
                                            
                                            {/* Submit Button for this Dept */}
                                            {canEditBlock && (
                                                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 4}}>
                                                    <button
                                                        className="smallbtn"
                                                        style={{
                                                            background: 'var(--ok)', 
                                                            color: '#000', 
                                                            border: 'none', 
                                                            padding: '6px 12px',
                                                            fontWeight: 'bold',
                                                            fontSize: '12px'
                                                        }}
                                                        onClick={() => {
                                                            const txt = commentMap[commentKey];
                                                            if(!txt || !txt.trim()) return alert("請填寫會辦意見後再送出");
                                                            if(!confirm(`確認以【${deptName}】身分送出會辦意見？送出後不可修改。`)) return;
                                                            
                                                            onDecision?.(stage.key, 'DONE', txt, deptId);
                                                        }}
                                                    >
                                                        送出會辦意見
                                                    </button>
                                                </div>
                                            )}

                                            {/* Timestamp Signature */}
                                            {isDeptDone && decision && (
                                                <div style={{fontSize: '11px', color: '#666', marginTop: 8, textAlign: 'right', borderTop: '1px dashed #333', paddingTop: 6}}>
                                                    {decision.decidedBy} • {new Date(decision.decidedAt!).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
              );
          }

          // --- Standard Stages Rendering ---
          
          return (
            <div key={stage.key} style={{display: 'flex', gap: '12px', zIndex: 1, opacity: (stage.status === 'NOT_STARTED' && !isCurrent) ? 0.5 : 1}}>
              <div style={{
                  width: '42px', height: '42px', borderRadius: '50%', 
                  background: isCurrent ? '#2563eb' : (isDone ? '#10b981' : '#2a2a2a'),
                  border: `2px solid ${isCurrent ? '#60a5fa' : (isDone ? '#10b981' : '#444')}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 'bold', fontSize: '14px', color: '#fff', flexShrink: 0
              }}>
                  {idx + 1}
              </div>

              <div style={{
                  flex: 1,
                  borderRadius: '8px',
                  border: `1px solid ${isCurrent ? '#60a5fa' : '#333'}`,
                  background: isCurrent ? 'rgba(37, 99, 235, 0.05)' : '#222',
                  padding: '12px'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                  <div style={{fontWeight: 500, fontSize: '14px', color: isCurrent ? '#60a5fa' : '#e5e7eb'}}>
                    {stage.label}
                  </div>
                  <div style={{display: 'flex', alignItems:'center', gap: 8}}>
                      <div style={{fontSize: '12px', color: statusColor, fontWeight: 'bold'}}>
                        {getStatusLabel(stage.status)}
                      </div>
                      
                      {/* Edit Button for completed stages */}
                      {userCanEdit && !isEditingThisStage && (
                          <button 
                            className="smallbtn" 
                            style={{fontSize: 10, padding: '2px 6px', background: 'transparent', border: '1px solid #555', color: '#ccc'}}
                            onClick={() => startEditing(stage)}
                            title="修正意見與核決"
                          >
                            ✎ 修改
                          </button>
                      )}
                  </div>
                </div>

                {(stage.approverName || stage.actedAt) && (
                    <div style={{fontSize: '11px', color: '#888', marginBottom: '8px'}}>
                        {stage.approverName || '簽核人'} • {stage.actedAt ? new Date(stage.actedAt).toLocaleString() : ''}
                    </div>
                )}

                {/* READ ONLY COMMENT */}
                {!isEditingThisStage && stage.comment && (
                  <div style={{marginTop: '10px'}}>
                    <div style={{fontSize: '12px', color: '#a5b4fc', marginBottom: '4px', fontWeight: 'bold'}}>
                        {commentLabel}
                    </div>
                    <div style={{fontSize: '13px', color: '#ddd', padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', whiteSpace: 'pre-wrap', borderLeft: '3px solid #666'}}>
                        {stage.comment}
                    </div>
                    {stage.edited && (
                        <div style={{fontSize:11, color:'#666', marginTop:4, textAlign:'right'}}>
                            (已修正) {stage.editedBy} • {stage.editedAt ? new Date(stage.editedAt).toLocaleString() : ''}
                        </div>
                    )}
                  </div>
                )}

                {/* KC Attachment Uploader Area (Legal Stage Only) */}
                {isLegalReview && (
                    <>
                        {/* 1. View List (Visible to all authorized participants if there are files) */}
                        {kcAttachments && kcAttachments.length > 0 && canViewAttachments && (
                            <div style={{marginTop: 12, borderTop: '1px dashed #555', paddingTop: 8}}>
                                <div style={{fontSize:12, color:'#a5b4fc', marginBottom:4}}>
                                    法務審查附件（關係人可見）：
                                </div>
                                <FileUploadKit 
                                    existingFiles={kcAttachments}
                                    disabled={!isLegalUser} // Only legal user can delete
                                    onRemoveFile={(f, i) => handleRemoveKcFile(i)}
                                />
                            </div>
                        )}

                        {/* 2. Upload Area (Visible ONLY to Legal during review) */}
                        {isLegalUser && isCurrent && (
                            <div style={{marginTop: 12, borderTop: '1px dashed #555', paddingTop: 8, background: 'rgba(0,0,0,0.2)', padding: 8, borderRadius: 6}}>
                                <FileUploadKit 
                                    label="上傳法務審查附件"
                                    existingFiles={[]} // Render existing above, only new here
                                    onFilesSelected={handleKcFileUpload}
                                    disabled={uploading}
                                />
                                {uploading && <div style={{fontSize:11, color:'#fbbf24'}}>上傳中...</div>}
                            </div>
                        )}
                    </>
                )}

                {/* EDITABLE COMMENT AREA (Active Approver OR Editing Mode) */}
                {(userCanAct || isEditingThisStage) && onDecision && (
                  <div style={{marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed rgba(255,255,255,0.1)'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <label className="label" style={{color: '#a5b4fc', marginBottom: '6px', display:'block', fontWeight: 'bold'}}>
                            {commentLabel} <span style={{color: 'var(--danger)'}}>*</span>
                        </label>
                        {isEditingThisStage && <span className="badge" style={{fontSize:10, background:'#333', color:'#fbbf24'}}>編輯模式</span>}
                    </div>
                    
                    <textarea
                      className="input"
                      placeholder={placeholder}
                      rows={4}
                      value={commentMap[stage.key] ?? ''}
                      onChange={(e) => setCommentMap({...commentMap, [stage.key]: e.target.value})}
                      style={{width: '100%', marginBottom: '8px', fontSize: '13px', minHeight: '80px', background: '#0b0b0c'}}
                    />
                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                      {isEditingThisStage && (
                          <button
                            className="smallbtn"
                            style={{background: 'transparent', border:'1px solid #666', color:'#ccc', padding: '4px 12px'}}
                            onClick={cancelEditing}
                          >
                            取消
                          </button>
                      )}
                      
                      <button
                        className="smallbtn"
                        style={{color: 'var(--danger)', borderColor: 'var(--danger)', padding: '4px 12px'}}
                        onClick={() => {
                            if(!commentMap[stage.key]) return alert("請填寫意見");
                            if(isEditingThisStage && !confirm("確定要將核決結果修改為「駁回」嗎？")) return;
                            onDecision(stage.key, 'REJECTED', commentMap[stage.key]);
                            if(isEditingThisStage) cancelEditing();
                        }}
                      >
                        駁回
                      </button>
                      
                      {/* Legal can "Return" */}
                      {stage.key === 'LEGAL_REVIEW' && (
                          <button
                            className="smallbtn"
                            style={{color: '#fbbf24', borderColor: '#fbbf24', padding: '4px 12px'}}
                            onClick={() => {
                                if(!commentMap[stage.key]) return alert("請填寫意見");
                                if(isEditingThisStage && !confirm("確定要將核決結果修改為「退回」嗎？")) return;
                                onDecision(stage.key, 'RETURNED', commentMap[stage.key]);
                                if(isEditingThisStage) cancelEditing();
                            }}
                          >
                            退回
                          </button>
                      )}
                      <button
                        className="smallbtn"
                        style={{background: 'var(--ok)', color: '#000', border: 'none', padding: '4px 16px'}}
                        onClick={() => {
                            if(!commentMap[stage.key]) return alert("請填寫意見（同意亦請填寫簡要說明）");
                            if(isEditingThisStage && !confirm("確定要儲存修改並設定為「核准」嗎？")) return;
                            onDecision(stage.key, 'APPROVED', commentMap[stage.key]);
                            if(isEditingThisStage) cancelEditing();
                        }}
                      >
                        {isEditingThisStage ? "儲存修改" : "核准 / 送出"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
